<template>
  <section>
    <Tabs>
      <TabsHeader tabs-header-class="atb-bg-white">
        <Tab
          v-for="(tab, index) in systemsStore.tabs"
          :key="tab.id"
          :index="index"
          :value="selectedTab"
          :closable="tab.name !== 'All Systems'"
          @tabClick="(index: number) => tabChange(index)"
          @close="(index: number) => closeTab(index, tab)"
        >
          {{ tab.name }}
          <sup v-if="index > 0 && getTabDirtyStatus(index, tab.id)">*</sup>
        </Tab>
        <confirmation-popup
          v-if="displayConfirmationPopup"
          :index="closeTabIndex"
          @closeActionPopup="displayConfirmationPopup = false"
          @closeConfirmationIndex="(index: number) => tabClose(index, systemsStore.tabs[index])"
        />
      </TabsHeader>
      <TabsBody>
        <TabPanelItem
          v-for="(tab, index) in systemsStore.tabs"
          :key="tab.id"
        >
          <keep-alive
            :key="index"
            v-if="index === 0"
          >
            <SystemsList
              ref="systemsListRef"
              :key="tab.id"
              v-if="selectedTab === index"
              :client-id="clientId"
              :system-id="tab.id"
              @create-new-tab="createNewTab"
              @create="createNewSystem"
              @copy="copySystem"
              @deploy="deployModalRef?.changeModalVisibility()"
              @delete="deleteModalRef?.changeModalVisibility()"
            />
          </keep-alive>
          <keep-alive
            :key="tab.id"
            v-else
          >
            <SystemsDetails
              :key="tab.id"
              v-if="selectedTab === index"
              :client-id="clientId"
              :system-id="tab.id"
              @system-deleted="systemDeleted"
            />
          </keep-alive>
        </TabPanelItem>
      </TabsBody>
    </Tabs>
    <SystemsDeploy ref="deployModalRef" />
    <SystemsDelete
      @delete="deleteSystem"
      :entity="'System'"
      ref="deleteModalRef"
    />
    <Modal
      :show="isDeleting"
      container-class="atb-w-1/3"
      body-class="atb-border-none"
      :closable="false"
    >
      <template #header>
        {{ $t('systemDeleteTitle') }}
      </template>
      <template #body>
        <div class="atb-w-full">
          <div class="progress">
            <label>{{ $t('pleaseBePatientDeleteInProgress') }}</label>
            <div class="progress__bar"></div>
          </div>
        </div>
      </template>
    </Modal>
  </section>
</template>

<script lang="ts" setup>
  import { Ref, ref, nextTick } from 'vue';
  import { storeToRefs } from 'pinia';
  import Tabs from '@atb-ui/eds-vue/src/components/Tabs/Tabs.vue';
  import Tab from '@atb-ui/eds-vue/src/components/Tabs/Tab.vue';
  import TabsHeader from '@atb-ui/eds-vue/src/components/Tabs/TabsHeader.vue';
  import TabsBody from '@atb-ui/eds-vue/src/components/Tabs/TabsBody.vue';
  import TabPanelItem from '@atb-ui/eds-vue/src/components/Tabs/TabPanelItem.vue';
  import { useSystemsStore } from '@/app/features/ams/systems/systems-store';
  import { useClientStore } from '@/app/features/ams/client/client-store';
  import { useAuthStore } from '@/app/shared/stores/auth-store';
  import SystemsList from '@/app/features/ams/systems/components/SystemsList.vue';
  import SystemsDetails from '@/app/features/ams/systems/components/SystemsDetails.vue';
  import systemsApi from '@/app/features/ams/systems/systems-service';
  import ConfirmationPopup from '@/app/shared/components/ConfirmationPopup.vue';
  import { ModalActions } from '@/app/shared/types/modal-actions.interface';
  import SystemsDeploy from '@/app/features/ams/systems/components/SystemsDeploy.vue';
  import { useAlertStore } from '@/app/shared/stores/alert-store';
  import { Motif } from '@/app/shared/enums/motif-enum';
  import { useI18n } from 'vue-i18n';
  import { System } from '@/app/shared/types/system.interface';
  import { AddTabEvent } from '@/app/shared/types/events/add-tab-event.interface';
  import SystemsDelete from '@/app/features/ams/shared/DeleteModal.vue';
  import Modal from '@atb-ui/eds-vue/src/components/Modal.vue';

  const { t } = useI18n();

  const systemsStore = useSystemsStore();
  const clientStore = useClientStore();
  const authStore = useAuthStore();
  const alertStore = useAlertStore();
  const closeTabIndex = ref(-1);
  const displayConfirmationPopup = ref(false);
  const clientId = clientStore.selectedClient.id;
  const { selectedTab } = storeToRefs(systemsStore);
  const deployModalRef: Ref<ModalActions | null> = ref(null);
  const deleteModalRef: Ref<ModalActions | null> = ref(null);
  const isDeleting = ref(false);
  const systemsListRef = ref(null) as Ref<any>;

  const getTabDirtyStatus = (_index: number, systemId: number) => {
    return systemsStore.systemDetails?.get(systemId)?.dirty;
  };

  const tabChange = async (index: number) => {
    if (index === 0) {
      selectedTab.value = index;
      nextTick(() => {
        systemsListRef.value[0]?.refresh();
      });
    } else {
      selectedTab.value = index;
    }
  };

  const closeTab = (index: number, tab: any) => {
    closeTabIndex.value = index;
    if (getTabDirtyStatus(index, tab.id)) {
      displayConfirmationPopup.value = true;
    } else {
      displayConfirmationPopup.value = false;
      tabClose(index, tab);
    }
  };

  const tabClose = (index: number, tab: any) => {
    displayConfirmationPopup.value = false;
    systemsStore.removeTabFromSystemNamesList(tab.id);
    //if tab is closed, select prev tab
    if (index <= selectedTab.value) {
      selectedTab.value -= 1;
      nextTick(() => {
        systemsListRef.value[0]?.refresh();
      });
    }
  };

  const createNewTab = async ({
    clientId,
    id,
    name,
  }: AddTabEvent): Promise<void> => {
    const tabIndex = systemsStore.tabs.findIndex((tab) => tab.name === name);
    if (tabIndex === -1) {
      await systemsStore.createSystemTab(clientId, {
        id,
        name,
        isNew: false,
        dirtyTab: new Map<string, boolean>(),
      });
      selectedTab.value = systemsStore.tabs.findIndex(
        (tab) => tab.name === name
      );
    } else {
      selectedTab.value = tabIndex;
    }
  };

  const createNewSystem = async () => {
    await systemsStore.createNewSystemTab(
      {
        id: -1,
        name: '',
        isNew: true,
        dirtyTab: new Map<string, boolean>(),
        isCopied: {
          isCopy: false,
          id: -1,
        },
      },
      {
        name: '',
        notes: '',
        createdAt: '',
        updatedAt: '',
        createdBy: authStore.username.split('@').shift(),
      }
    );
    selectedTab.value = systemsStore.tabs.length - 1;
  };

  const copySystem = async ({ id, notes }: System) => {
    await systemsStore.createNewSystemTab(
      {
        id,
        name: '',
        isNew: true,
        dirtyTab: new Map<string, boolean>(),
        isCopied: {
          id,
          isCopy: true,
        },
      },
      {
        notes,
        name: '',
        createdAt: '',
        updatedAt: '',
        createdBy: authStore.username.split('@').shift(),
      }
    );
    selectedTab.value = systemsStore.tabs.length - 1;
  };

  const deleteSystem = async () => {
    const { id } =
      systemsListRef.value[0].gridApi?.getSelectedRows()?.shift() ?? {};
    try {
      isDeleting.value = true;
      await systemsStore.deleteSystem(clientId, id);
      alertStore.showNotification(
        t('systemDeleted'),
        t('systems'),
        Motif.SUCCESS
      );
      systemsStore.deleteAttribute(id);
      systemsListRef.value[0].gridApi?.deselectAll();
      systemsListRef.value[0].refresh();
    } catch {
      alertStore.showNotification(t('unknownError'), t('systems'), Motif.ERROR);
    } finally {
      isDeleting.value = false;
    }
  };

  const systemDeleted = () => {
    selectedTab.value = 0;
    alertStore.showNotification(
      t('systemDeleted'),
      t('systems'),
      Motif.SUCCESS
    );
    nextTick(() => {
      systemsListRef.value[0]?.refresh();
    });
  };
</script>

<style>
  .progress {
    margin: 50px auto;
    padding: 2px;
    width: 100%;
    max-width: 500px;
    height: 20px;
  }

  .progress .progress__bar {
    height: 100%;
    width: 0%;
    background-color: #426da9;
    animation: fill-bar 3s infinite;
  }

  @keyframes fill-bar {
    from {
      width: 0%;
    }
    to {
      width: 100%;
    }
  }
</style>
